import { useAppSelector } from 'src/hooks/useAppSelector';
import { userSettingsSelector } from 'src/store/slices/users';
import { get } from 'lodash';
import moment from 'moment';
import { defaultGlobalSettings, FormatterService } from 'lca-common-ui-components';

export const useUserSettings = () => {
  const data = useAppSelector(userSettingsSelector);

  const dateTimeFormat: string = get(
    data,
    'global.dateTimeFormat',
    defaultGlobalSettings.global.dateTimeFormat,
  );
  const emptyValue: string = get(data, 'global.emptyValue', defaultGlobalSettings.global.emptyValue);
  const precision: number = get(data, 'global.precision', defaultGlobalSettings.global.precision);

  const getFormattedDateTime = (date?: string, format?: string) => {
    if (!moment(date).isValid()) {
      return emptyValue;
    }
    const momentFormat = FormatterService.convertDotNetToMomentFormat(dateTimeFormat);
    return moment(date).format(format ?? momentFormat);
  };

  const hasNoExpiringDate = (date?: string) => {
    return date && moment(date).isSameOrAfter('9999-12-31', 'day');
  };

  const getFormattedExpirationDateTime = (date?: string, format?: string) => {
    return hasNoExpiringDate(date)
      ? 'not expiring'
      : getFormattedDateTime(date, format);
  };

  const getValueOrDefault = <T>(value?: T): string | T => {
    return typeof value === 'undefined' || value === '' || value === null ? emptyValue : value;
  };

  const getFormattedNumber = (n?: number) => {
    return typeof n !== 'undefined'
      ? FormatterService.displayNumberFormatter(n, precision)
      : emptyValue;
  };

  const getLocalizedNumber = (number?: number | undefined) => {
    number = number ?? 0;
    return number.toLocaleString();
  };

  return {
    emptyValue,
    getFormattedDateTime,
    getFormattedExpirationDateTime,
    hasNoExpiringDate,
    getValueOrDefault,
    getFormattedNumber,
    getLocalizedNumber,
  };
};
